import { ChargePointType } from './../stores/small-biz-store.model';
import { map } from 'rxjs/operators';
import { SmallBizStoreService } from './../stores/small-biz-store.service';
import { ChargingStationPricing } from '../libs/feature-lib/charging-station/charging-station-pricing.service';
import { ProductsStoreService } from './../stores/products-store.service';
import { TranslateService } from '@ngx-translate/core';
import { AnalyticsService } from '@app/shared/modules/analytics/services/analytics.service';
import { Injectable } from '@angular/core';
import { environment } from '@app/env';

type AnalyticsWorkflowEvent = {
  event: string;
  page: {
    language: string;
    category: string;
    publicTarget: string;
  };
  workflow: {
    stepName: string;
  };
  simulator: {
    addedProductType: string;
    chargingCardNumber: string | number;
    chargingCardPrice: number;
    chargingStationNumber: string | number;
    chargingStationType?: 'connected 30' | 'connected 50';
    chargingStationPrice: number;
    option?: 'smart-option';
    stationLocation?: string;
    result?: number;
    leadId?: string;
  };
  product: {
    section: string;
    branch: string;
  };
};

@Injectable({
  providedIn: 'root',
})
export class AnalyticsHelperService {
  constructor(
    private store: SmallBizStoreService,
    private chargingStationPricing: ChargingStationPricing,
    private productsStoreService: ProductsStoreService,
    private analyticsService: AnalyticsService,
    private translate: TranslateService
  ) { }

  public async analyticsWorkflowStep(
    stepName: string,
    options: { withResult?: boolean; leadId?: string; event?: string; maxCards?: boolean; maxStations?: boolean } = {
      maxCards: false,
      maxStations: false
    }
  ) {
    const model = this.store.model;

    const event: AnalyticsWorkflowEvent = {
      event: options.event || 'b-to-b-ev-simulator-step',
      page: {
        language: this.translate.currentLang,
        category: 'b-to-b-ev-simulator',
        publicTarget: 'pro-sme',
      },
      workflow: {
        stepName,
      },
      simulator: {
        addedProductType: 'none',
        chargingCardNumber: 0,
        chargingCardPrice: 0,
        chargingStationNumber: 0,
        chargingStationPrice: 0,
      },
      product: {
        section: 'my-drive',
        branch: 'ev',
      },
    };

    if (model.chargeCardsAmount > 0) {
      event.simulator.addedProductType = 'charging-card';
      event.simulator.chargingCardNumber = model.chargeCardsAmount;

      const chargingCardPrice = await this.productsStoreService.card$
        .pipe(map((card) => parseInt(card.price, 10)))
        .toPromise();

      event.simulator.chargingCardPrice = chargingCardPrice * model.chargeCardsAmount;
    }

    if (options.maxCards) {
      event.simulator.addedProductType = 'charging-card';
      event.simulator.chargingCardNumber = `more than ${environment.maxChargingCards}`;
    }

    if (model.chargingStationConfig.amount > 0) {
      event.simulator.addedProductType = 'charging-station';
      event.simulator.chargingStationNumber = model.chargingStationConfig.amount;

      switch (model.chargingStationConfig.type) {
        case ChargePointType.connect30:
        case ChargePointType.connect30Pro:
          event.simulator.chargingStationType = 'connected 30';
          break;
        case ChargePointType.connect50:
        case ChargePointType.connect50Pro:
          event.simulator.chargingStationType = 'connected 50';
      }

      const location = await this.productsStoreService.findByLocation(model.chargingStationConfig.location).toPromise();
      event.simulator.stationLocation = location?.code;

      const price = await this.chargingStationPricing.for$(model.chargingStationConfig).toPromise();
      event.simulator.chargingStationPrice = price;

      if (model.chargingStationConfig.smart) {
        event.simulator.option = 'smart-option';
      }
    }

    if (options.maxStations) {
      event.simulator.addedProductType = 'charging-station';
      event.simulator.chargingStationNumber = `more than ${environment.maxChargingStations}`;
    }

    const multipleCardsAndStations = model.chargeCardsAmount > 0 && model.chargingStationConfig.amount > 0;
    const maxCardsAndStations = options.maxCards && model.chargingStationConfig.amount > 0;
    const maxStationsAndCards = options.maxCards && model.chargeCardsAmount > 0;
    if (multipleCardsAndStations || maxCardsAndStations || maxStationsAndCards) {
      event.simulator.addedProductType = 'multiple';
    }

    if (options.withResult) {
      event.simulator.result = event.simulator.chargingCardPrice + event.simulator.chargingStationPrice;
    }

    if (options.leadId) {
      event.simulator.leadId = options.leadId;
    }

    this.analyticsService.push(event);
  }
}
